<template>
  <transition name="cd-pwa-slide">
    <div
      v-if="visible"
      class="cd-pwa-banner"
      :class="{ 'cd-pwa-banner--lifted': liftForBottomNav }"
      role="dialog"
      aria-label="Install Chabad Digital app"
    >
      <button class="cd-pwa-close" aria-label="Dismiss" @click="dismiss">
        <i class="pi pi-times"></i>
      </button>

      <div class="cd-pwa-icon">
        <img :src="brandIcon" alt="Chabad Digital" />
      </div>

      <div class="cd-pwa-body">
        <p class="cd-pwa-title">Install Chabad Digital</p>

        <template v-if="mode === 'prompt'">
          <p class="cd-pwa-text">
            Add Chabad Digital to your device for a faster, full-screen, app-like
            experience.
          </p>
          <div class="cd-pwa-actions">
            <button class="cd-pwa-btn cd-pwa-btn--primary" @click="install">
              Install app
            </button>
            <button class="cd-pwa-btn cd-pwa-btn--ghost" @click="dismiss">
              Not now
            </button>
          </div>
        </template>

        <template v-else-if="mode === 'ios'">
          <p class="cd-pwa-text">
            Tap
            <span class="cd-pwa-inline">
              <i class="pi pi-upload"></i> Share
            </span>
            then
            <span class="cd-pwa-inline">Add to Home Screen</span>
            to install.
          </p>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  DISPLAY_ROUTE,
  SCREEN_SCHEDULE_DISPLAY_ROUTE,
  YURTZEITEN_PUBLIC_FORM_ROUTE,
  YURTZEITEN_PUBLIC_FORM_LEGACY_ROUTE,
} from '@/router/routes';
import brandIcon from '@/assets/images/chabad-digital-icon.png';
import {
  installState,
  isIosSafari,
  isStandalone,
  promptInstall,
} from '@/pwa/install_prompt';

const DISMISS_KEY = 'cd_pwa_install_dismissed_at';
// Once dismissed, stay quiet for this long before offering again.
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// Let the app settle before the iOS hint appears — it is a static tip rather
// than a response to anything the browser told us, so it should not race the
// first paint.
const IOS_HINT_DELAY_MS = 2500;

// Pages the banner must never cover:
//  - Display is the unattended TV/kiosk view — a screen's link or a screen
//    schedule's link,
//  - the public yahrzeit form is opened from a QR code by congregants who are
//    filling in one form, not using the app.
const SUPPRESSED_ROUTES = [
  DISPLAY_ROUTE,
  SCREEN_SCHEDULE_DISPLAY_ROUTE,
  YURTZEITEN_PUBLIC_FORM_ROUTE,
  YURTZEITEN_PUBLIC_FORM_LEGACY_ROUTE,
];

export default {
  name: 'PwaInstallPrompt',
  props: {
    // Raises the banner clear of the mobile bottom navigation when one is
    // showing. App.vue already decides that, so it is passed in rather than
    // re-derived from the route here.
    liftForBottomNav: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      brandIcon,
      installState,
      // 'ios' -> manual Safari hint. The native prompt mode is derived from the
      // captured `beforeinstallprompt` event instead (see `mode`).
      iosHint: false,
      dismissed: false,
      iosHintTimer: null,
    };
  },
  watch: {
    // Once the app is actually installed the stored "not now" is meaningless.
    // Clearing it means that if the user later uninstalls and comes back, the
    // offer returns straight away instead of being sat on for the rest of the
    // 7-day window.
    'installState.installed'(installed) {
      if (!installed) return;
      try {
        window.localStorage.removeItem(DISMISS_KEY);
      } catch (e) {
        /* ignore */
      }
    },
  },
  computed: {
    // 'prompt' -> native install available; 'ios' -> manual Safari hint.
    mode() {
      if (this.installState.canInstall) return 'prompt';
      if (this.iosHint) return 'ios';
      return null;
    },
    onSuppressedRoute() {
      const route = this.$route;
      if (!route) return false;
      if (SUPPRESSED_ROUTES.includes(route.name)) return true;
      // Path fallback (`/display/...`) so the banner is suppressed even if the
      // route name isn't resolved as expected.
      const path = String(route.path || '');
      return path === '/display' || path.startsWith('/display/');
    },
    visible() {
      // Reactive to route changes, so a prompt armed elsewhere stays hidden
      // while on a suppressed page and reappears after leaving it.
      if (this.onSuppressedRoute) return false;
      if (this.installState.installed || this.dismissed) return false;
      return this.mode != null;
    },
  },
  methods: {
    recentlyDismissed() {
      try {
        const raw = window.localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const at = parseInt(raw, 10);
        if (!at) return false;
        return Date.now() - at < DISMISS_WINDOW_MS;
      } catch (e) {
        return false;
      }
    },
    rememberDismissal() {
      try {
        window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch (e) {
        /* ignore */
      }
    },
    async install() {
      const outcome = await promptInstall();
      // Only a real "no" earns the 7-day cooldown. 'accepted' needs no
      // bookkeeping — the `appinstalled` event flips installState.installed —
      // and 'unavailable' means the browser let us down, not the user, so the
      // offer should come back on the next visit rather than being buried.
      if (outcome === 'dismissed') this.rememberDismissal();
      this.dismissed = true;
    },
    dismiss() {
      this.rememberDismissal();
      this.dismissed = true;
    },
    maybeShowIosHint() {
      if (!isIosSafari()) return;
      this.iosHintTimer = window.setTimeout(() => {
        this.iosHintTimer = null;
        if (!this.installState.installed) this.iosHint = true;
      }, IOS_HINT_DELAY_MS);
    },
  },
  mounted() {
    if (typeof window === 'undefined') return;
    // A tab opened in standalone mode never sees `beforeinstallprompt`, but
    // re-check here in case the app was launched straight from the home screen.
    if (isStandalone()) {
      this.installState.installed = true;
      return;
    }
    if (this.recentlyDismissed()) {
      this.dismissed = true;
      return;
    }
    this.maybeShowIosHint();
  },
  beforeUnmount() {
    if (this.iosHintTimer != null) {
      window.clearTimeout(this.iosHintTimer);
      this.iosHintTimer = null;
    }
  },
};
</script>

<style scoped>
.cd-pwa-banner {
  position: fixed;
  /* Sits in the gap between the mobile bottom nav (900) and the first overlay
     layer: every modal in this app is 1000, the bottom-nav "More" sheet is 1200
     and the route spinner is 9999. The banner must stay BELOW all of those — it
     is an opaque, tappable surface pinned to the bottom edge, so a higher value
     would let it cover the "More" sheet's lower rows (the only way to reach
     Settings and Signout on a phone) and modal footer buttons. It never needs to
     outrank the bottom nav geometrically either; `--lifted` already clears it. */
  z-index: 950;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 420px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: var(--color-bg-canvas, #ffffff);
  border: 1px solid var(--color-primary-border, #c7d2fe);
  border-radius: var(--radius-xl, 16px);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
  font-family: var(--font-sans, 'Poppins', system-ui, -apple-system, sans-serif);
}

/* The bottom nav is 64px tall plus the device's bottom safe area; clear both
   so the banner never sits on top of it. */
.cd-pwa-banner--lifted {
  bottom: calc(76px + env(safe-area-inset-bottom, 0px));
}

/* The bottom nav itself only renders below 992px, so above that the banner
   returns to the viewport bottom even when App.vue says it is showing. */
@media (min-width: 992px) {
  .cd-pwa-banner--lifted {
    bottom: 20px;
  }
}

.cd-pwa-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--color-text-muted, #94a3b8);
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  line-height: 1;
}
.cd-pwa-close:hover {
  background: var(--color-bg-muted, #f1f5f9);
  color: var(--color-text-secondary, #475569);
}

.cd-pwa-icon {
  flex: 0 0 auto;
}
.cd-pwa-icon img {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-lg, 12px);
  display: block;
  object-fit: contain;
}

.cd-pwa-body {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 18px;
}

.cd-pwa-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary, #0f172a);
}

.cd-pwa-text {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: var(--color-text-secondary, #475569);
}

.cd-pwa-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: var(--radius-sm, 6px);
  background: var(--color-primary-soft, #eef2ff);
  color: var(--color-primary-hover, #4f46e5);
  font-weight: 600;
  white-space: nowrap;
}
.cd-pwa-inline .pi {
  font-size: 0.72rem;
}

.cd-pwa-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.cd-pwa-btn {
  border: none;
  border-radius: var(--radius-md, 10px);
  padding: 9px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.cd-pwa-btn--primary {
  background: var(--color-primary, #4841e7);
  color: var(--color-text-on-primary, #ffffff);
}
.cd-pwa-btn--primary:hover {
  background: var(--color-primary-hover, #3a34d4);
}
.cd-pwa-btn--ghost {
  background: transparent;
  color: var(--color-text-secondary, #64748b);
}
.cd-pwa-btn--ghost:hover {
  background: var(--color-bg-muted, #f1f5f9);
}

/* slide-up transition */
.cd-pwa-slide-enter-active,
.cd-pwa-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.cd-pwa-slide-enter-from,
.cd-pwa-slide-leave-to {
  transform: translate(-50%, 24px);
  opacity: 0;
}

/* The app ships a light theme only, so these are literals rather than tokens —
   they exist so the banner still reads correctly for a viewer whose OS is in
   dark mode and whose browser darkens the surrounding chrome. */
@media (prefers-color-scheme: dark) {
  .cd-pwa-banner {
    background: #1e293b;
    border-color: rgba(129, 140, 248, 0.3);
  }
  /* The brand mark is a deep blue/violet drawn for light surfaces; a white
     plate keeps it legible against the dark banner. */
  .cd-pwa-icon img {
    background: #ffffff;
    padding: 6px;
  }
  .cd-pwa-title {
    color: #f8fafc;
  }
  .cd-pwa-text {
    color: #cbd5e1;
  }
  .cd-pwa-close {
    color: #94a3b8;
  }
  .cd-pwa-close:hover {
    background: #334155;
    color: #e2e8f0;
  }
  .cd-pwa-inline {
    background: #312e81;
    color: #c7d2fe;
  }
  .cd-pwa-btn--ghost {
    color: #cbd5e1;
  }
  .cd-pwa-btn--ghost:hover {
    background: #334155;
  }
}
</style>
